import { useCallback } from 'react'
import {
	ChevronRightIcon,
	CircleArrowUpIcon,
	LayersIcon,
	MoreVerticalIcon,
	PencilIcon,
	PlusIcon,
	SearchIcon,
	SquarePenIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplyScenario } from '@/features/scenario/apply'
import { CreateScenario } from '@/features/scenario/create'
import { DeleteScenario } from '@/features/scenario/delete'
import { DuplicateScenario } from '@/features/scenario/duplicate'
import { EditScenario } from '@/features/scenario/edit'

import CheckWaveIcon from '@/shared/components/icons/CheckWaveIcon'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
} from '@/shared/components/ui/sidebar'
import { Tag } from '@/shared/components/ui/tag'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'

import { RenameScenario } from './scenario/RenameScenario'
import { useScenarios } from './ScenariosProvider'
import { ScenariosSidebarToolbar } from './ScenariosSidebarToolbar'

export const ScenariosSidebar = () => {
	const { t } = useTranslation()

	const {
		groupedScenarios,
		isLoading,
		replaceLastPanel,
		isGroupOpen,
		setOpenGroups,
		toggleScenarioSelection,
		selectedScenarioIds,
	} = useScenarios()
	const handleGroupOpenChange = useCallback(
		(groupKey: string) => (open: boolean) => {
			setOpenGroups((prev) => ({
				...prev,
				[groupKey]: open,
			}))
		},
		[]
	)
	const handleViewScenarioDetails = useCallback(
		(scenarioId: string) => {
			ampli.scenariosSidebarSelectScenario()
			replaceLastPanel({
				scenarioId,
				part: 'settings',
				pricingCampaignId: null,
			})
		},
		[replaceLastPanel]
	)

	return (
		<Sidebar collapsible='none' className='w-full shrink-0 border-r'>
			<ScenariosSidebarToolbar />
			<SidebarContent>
				{isLoading ? (
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								<LoaderPlaceholder />
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				) : groupedScenarios?.length ? (
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								{groupedScenarios.map((group) => {
									const groupKey = `${group.name}-${group.id}`

									return (
										<SidebarMenuItem key={groupKey}>
											<Collapsible
												open={isGroupOpen(groupKey)}
												onOpenChange={handleGroupOpenChange(groupKey)}
												className='group/collapsible [&[data-state=open]_[data-slot=icon]]:rotate-90'
											>
												<div className='sticky top-0 z-10 w-full items-center bg-sidebar'>
													<CollapsibleTrigger asChild>
														<SidebarMenuButton size='sm'>
															<ChevronRightIcon
																className='transition-transform'
																data-slot='icon'
															/>
															<Tooltip>
																<TooltipTrigger asChild>
																	<span>{group.name}</span>
																</TooltipTrigger>
																<TooltipContent>{group.name}</TooltipContent>
															</Tooltip>
														</SidebarMenuButton>
													</CollapsibleTrigger>
													<CreateScenario optimizationGroupId={group.id}>
														<SidebarMenuAction
															showOnHover
															className='cursor-pointer items-center'
														>
															<Tooltip>
																<TooltipTrigger asChild>
																	<PlusIcon />
																</TooltipTrigger>
																<TooltipContent>
																	{t('scenario.add')}
																</TooltipContent>
															</Tooltip>
														</SidebarMenuAction>
													</CreateScenario>
												</div>
												<CollapsibleContent>
													<SidebarMenuSub>
														{group?.scenarios?.map((scenario) => (
															<SidebarMenuItem key={scenario.id}>
																<div className='flex items-center gap-1'>
																	<SidebarMenuButton
																		size='sm'
																		onClick={() =>
																			handleViewScenarioDetails(scenario.id)
																		}
																		className='cursor-pointer'
																	>
																		<Checkbox
																			data-testid='scenario-checkbox'
																			checked={selectedScenarioIds.includes(
																				scenario.id
																			)}
																			onClick={(e) => e.stopPropagation()}
																			onChange={() =>
																				toggleScenarioSelection(scenario.id)
																			}
																		/>
																		<span className='truncate'>
																			{scenario.name}
																		</span>{' '}
																		{scenario.is_main && (
																			<Tooltip>
																				<TooltipTrigger asChild>
																					<CheckWaveIcon />
																				</TooltipTrigger>
																				<TooltipContent>
																					{t('app.live')}
																				</TooltipContent>
																			</Tooltip>
																		)}
																		<div className='ml-auto flex items-center gap-2'>
																			{scenario.scenario_type ===
																				'recommendation' && (
																				<Tag variant='epic' size='sm'>
																					{t('app.ai')}
																				</Tag>
																			)}
																		</div>
																	</SidebarMenuButton>
																	<DropdownMenu>
																		<DropdownMenuTrigger asChild>
																			<SidebarMenuAction
																				showOnHover
																				className='cursor-pointer'
																			>
																				<MoreVerticalIcon />
																			</SidebarMenuAction>
																		</DropdownMenuTrigger>
																		<DropdownMenuContent
																			side='bottom'
																			align='end'
																		>
																			<ApplyScenario scenario={scenario}>
																				<DropdownMenuItem
																					disabled={scenario.is_main}
																				>
																					<CircleArrowUpIcon />
																					{t('scenario.apply')}
																				</DropdownMenuItem>
																			</ApplyScenario>
																			<DropdownMenuSeparator />
																			<RenameScenario scenario={scenario}>
																				<DropdownMenuItem
																					disabled={scenario.is_main}
																				>
																					<PencilIcon />
																					{t('app.rename', {
																						defaultValue: 'Rename',
																					})}
																				</DropdownMenuItem>
																			</RenameScenario>
																			<EditScenario scenarioId={scenario.id}>
																				<DropdownMenuItem
																					disabled={scenario.is_main}
																				>
																					<SquarePenIcon />
																					{t('scenario.edit')}
																				</DropdownMenuItem>
																			</EditScenario>
																			<DuplicateScenario
																				scenarioId={scenario.id}
																			>
																				<DropdownMenuItem>
																					<LayersIcon />
																					{t('scenario.duplicate')}
																				</DropdownMenuItem>
																			</DuplicateScenario>
																			<DeleteScenario scenarioId={scenario.id}>
																				<DropdownMenuItem
																					disabled={scenario.is_main}
																					variant='destructive'
																				>
																					<Trash2Icon />
																					{t('scenario.delete')}
																				</DropdownMenuItem>
																			</DeleteScenario>
																		</DropdownMenuContent>
																	</DropdownMenu>
																</div>
															</SidebarMenuItem>
														))}
													</SidebarMenuSub>
												</CollapsibleContent>
											</Collapsible>
										</SidebarMenuItem>
									)
								})}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				) : (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<SearchIcon className='text-muted-foreground' />
							</EmptyMedia>
							<EmptyDescription>{t('no_scenarios')}</EmptyDescription>
						</EmptyHeader>
					</Empty>
				)}
			</SidebarContent>
		</Sidebar>
	)
}

const LoaderPlaceholder = () => (
	<>
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
		<SidebarMenuSkeleton showIcon />
	</>
)
